import { useRef, useState, useEffect } from "react";
import "../createPostPopup/style.css";
import "./style.css";
import EmojiPickerBackgrounds from "../createPostPopup/EmojiPickerBackgrounds";
import AddToYourPost from "../createPostPopup/AddToYourPost";
import ImagePreview from "../createPostPopup/ImagePreview";
import useClickOutside from "../../helpers/clickOutside";
import { updatePost } from "../../functions/post";
import PulseLoader from "react-spinners/PulseLoader";
import PostError from "../createPostPopup/PostError";
import dataURItoBlob from "../../helpers/dataURItoBlob";
import { uploadPostMedia } from "../../functions/uploadImages";
import {
  getEditorialType,
  typeRequiresVideo,
  typeRequiresAudio,
  typeRequiresImage,
} from "../../config/editorialRegistry";
import {
  // Social & Media
  ThreadsForm,
  LiveAudioForm,
  SpotifyForm,
  AudioForm,
  StickerForm,
  GifForm,
  // Video & Streaming
  TikTokForm,
  YouTubeForm,
  NetflixForm,
  VideoBankForm,
  LiveStreamForm,
  MoviePortalForm,
  // Gaming
  GamingForm,
  EsportsForm,
  GameReviewForm,
  // News & Content
  CNNForm,
  BlogForm,
  PodcastForm,
  PortfolioForm,
  LibraryForm,
  EbookForm,
  PdfForm,
  // Education
  CourseForm,
  QuizForm,
  TutorialForm,
  EstudentForm,
  SchoolForm,
  VotingForm,
  // Commerce
  EShopForm,
  AliExpressForm,
  RestaurantForm,
  FoodDeliveryForm,
  BookingForm,
  AirbnbForm,
  RentalForm,
  UberForm,
  JobServiceForm,
  EventForm,
  ClassifiedForm,
  // Health & Social
  MedicalForm,
  OngForm,
  CrowdfundingForm,
  CommunityForm,
  PetitionForm,
  // Wellness & Lifestyle
  TravelForm,
  CarForm,
  YogaForm,
  ExerciseForm,
  RecipeForm,
  FashionForm,
  BeautyForm,
} from "../createPostPopup/editorial";

// Map editorial types to their data field names
const dataFieldMap = {
  threads: "threadsData",
  live_audio: "liveAudioData",
  spotify: "spotifyData",
  audio: "audioData",
  sticker: "stickerData",
  gif: "gifData",
  tiktok: "tiktokData",
  reels: "tiktokData",
  shorts: "tiktokData",
  youtube: "youtubeData",
  netflix: "netflixData",
  video_bank: "videoBankData",
  live_streaming: "liveStreamData",
  movie_portal: "moviePortalData",
  gaming: "gamingData",
  esports: "esportsData",
  game_review: "gameReviewData",
  cnn: "cnnData",
  blog: "blogData",
  podcast: "podcastData",
  portfolio: "portfolioData",
  library: "libraryData",
  ebook: "ebookData",
  pdf: "pdfData",
  course: "courseData",
  estudent: "estudentData",
  school: "schoolData",
  quiz: "quizData",
  voting: "votingData",
  tutorial: "tutorialData",
  eshop: "eshopData",
  aliexpress: "aliexpressData",
  restaurant: "restaurantData",
  food_delivery: "foodDeliveryData",
  booking: "bookingData",
  airbnb: "airbnbData",
  rental: "rentalData",
  uber: "uberData",
  job_service: "jobServiceData",
  event: "eventData",
  classified: "classifiedData",
  medical: "medicalData",
  ong: "ongData",
  crowdfunding: "crowdfundingData",
  community: "communityData",
  petition: "petitionData",
  travel: "travelData",
  car: "carData",
  yoga: "yogaData",
  exercise: "exerciseData",
  recipe: "recipeData",
  fashion: "fashionData",
  beauty: "beautyData",
};

export default function EditPostPopup({
  post,
  user,
  setVisible,
  onPostUpdated,
}) {
  const popup = useRef(null);
  const [text, setText] = useState(post?.text || "");
  const [showPrev, setShowPrev] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState(post?.images || []);
  const [existingVideos, setExistingVideos] = useState(post?.videos || []);
  const [mediaTypes, setMediaTypes] = useState([]);
  const [background, setBackground] = useState(post?.background || "");
  const editorialType = post?.editorialType || "facebook";

  // Get existing editorial data from the post
  const getExistingEditorialData = () => {
    const dataField = dataFieldMap[editorialType];
    if (dataField && post?.[dataField]) {
      return post[dataField];
    }
    return {};
  };

  const [editorialData, setEditorialData] = useState(getExistingEditorialData());

  useEffect(() => {
    // Show image preview if post has images or videos
    if ((post?.images?.length > 0) || (post?.videos?.length > 0)) {
      setShowPrev(true);
    }
  }, [post]);

  useClickOutside(popup, () => {
    setVisible(false);
  });

  const getEditorialTypeName = () => {
    const type = getEditorialType(editorialType);
    return type ? type.name : "Post";
  };

  const getEditorialTypeColor = () => {
    const type = getEditorialType(editorialType);
    return type?.color || "#1877f2";
  };

  // Validation by editorial type
  const validateEditorialData = () => {
    const errors = [];
    const hasNewImages = images && images.length > 0;
    const hasExistingMedia = (existingImages?.length > 0) || (existingVideos?.length > 0);
    const hasMedia = hasNewImages || hasExistingMedia;

    if (typeRequiresVideo(editorialType) && !hasMedia) {
      const typeName = getEditorialTypeName();
      errors.push(`${typeName} requer um video`);
    }

    if (typeRequiresAudio(editorialType) && !hasMedia) {
      const typeName = getEditorialTypeName();
      errors.push(`${typeName} requer um audio`);
    }

    if (typeRequiresImage(editorialType) && !hasMedia && editorialType !== "facebook") {
      const typeName = getEditorialTypeName();
      errors.push(`${typeName} requer pelo menos uma imagem`);
    }

    // Type-specific validations
    switch (editorialType) {
      case "youtube":
        if (!editorialData.title?.trim()) {
          errors.push("YouTube requer um titulo");
        }
        break;

      case "netflix":
        if (!editorialData.title?.trim()) {
          errors.push("Netflix requer um titulo");
        }
        if (!editorialData.contentType) {
          errors.push("Netflix requer o tipo de conteudo (Filme/Serie)");
        }
        break;

      case "cnn":
        if (!editorialData.headline?.trim()) {
          errors.push("Noticia requer uma manchete");
        }
        if (!text?.trim() && !editorialData.content?.trim()) {
          errors.push("Noticia requer conteudo");
        }
        break;

      case "blog":
        if (!editorialData.title?.trim()) {
          errors.push("Blog requer um titulo");
        }
        if (!text?.trim() && !editorialData.content?.trim()) {
          errors.push("Blog requer conteudo");
        }
        break;

      case "podcast":
        if (!editorialData.title?.trim()) {
          errors.push("Podcast requer um titulo");
        }
        break;

      case "eshop":
        if (!editorialData.productName?.trim()) {
          errors.push("Produto requer um nome");
        }
        if (!editorialData.price?.amount || editorialData.price.amount <= 0) {
          errors.push("Produto requer um preco valido");
        }
        break;

      case "aliexpress":
        if (!editorialData.productName?.trim()) {
          errors.push("Produto requer um nome");
        }
        if (!editorialData.price?.amount || editorialData.price.amount <= 0) {
          errors.push("Produto requer um preco valido");
        }
        break;

      case "gaming":
        if (!editorialData.game?.trim()) {
          errors.push("Gaming requer o nome do jogo");
        }
        break;

      case "facebook":
      default:
        if (!text?.trim() && !background && !hasMedia) {
          errors.push("Adicione texto, imagem ou escolha um fundo");
        }
        break;
    }

    return errors;
  };

  const canSubmit = () => {
    const errors = validateEditorialData();
    return errors.length === 0;
  };

  const renderEditorialForm = () => {
    const formProps = {
      data: editorialData,
      onChange: setEditorialData,
      editorialType,
    };

    switch (editorialType) {
      // Social & Media
      case "threads":
        return <ThreadsForm {...formProps} />;
      case "live_audio":
        return <LiveAudioForm {...formProps} />;
      case "spotify":
        return <SpotifyForm {...formProps} />;
      case "audio":
        return <AudioForm {...formProps} />;
      case "sticker":
        return <StickerForm {...formProps} />;
      case "gif":
        return <GifForm {...formProps} />;
      // Video & Streaming
      case "tiktok":
      case "reels":
      case "shorts":
        return <TikTokForm {...formProps} />;
      case "youtube":
        return <YouTubeForm {...formProps} />;
      case "netflix":
        return <NetflixForm {...formProps} />;
      case "video_bank":
        return <VideoBankForm {...formProps} />;
      case "live_streaming":
        return <LiveStreamForm {...formProps} />;
      case "movie_portal":
        return <MoviePortalForm {...formProps} />;
      // Gaming
      case "gaming":
        return <GamingForm {...formProps} />;
      case "esports":
        return <EsportsForm {...formProps} />;
      case "game_review":
        return <GameReviewForm {...formProps} />;
      // News & Content
      case "cnn":
        return <CNNForm {...formProps} />;
      case "blog":
        return <BlogForm {...formProps} />;
      case "podcast":
        return <PodcastForm {...formProps} />;
      case "portfolio":
        return <PortfolioForm {...formProps} />;
      case "library":
        return <LibraryForm {...formProps} />;
      case "ebook":
        return <EbookForm {...formProps} />;
      case "pdf":
        return <PdfForm {...formProps} />;
      // Education
      case "course":
        return <CourseForm {...formProps} />;
      case "quiz":
        return <QuizForm {...formProps} />;
      case "tutorial":
        return <TutorialForm {...formProps} />;
      case "estudent":
        return <EstudentForm {...formProps} />;
      case "school":
        return <SchoolForm {...formProps} />;
      case "voting":
        return <VotingForm {...formProps} />;
      // Commerce
      case "eshop":
        return <EShopForm {...formProps} />;
      case "aliexpress":
        return <AliExpressForm {...formProps} />;
      case "restaurant":
        return <RestaurantForm {...formProps} />;
      case "food_delivery":
        return <FoodDeliveryForm {...formProps} />;
      case "booking":
        return <BookingForm {...formProps} />;
      case "airbnb":
        return <AirbnbForm {...formProps} />;
      case "rental":
        return <RentalForm {...formProps} />;
      case "uber":
        return <UberForm {...formProps} />;
      case "job_service":
        return <JobServiceForm {...formProps} />;
      case "event":
        return <EventForm {...formProps} />;
      case "classified":
        return <ClassifiedForm {...formProps} />;
      // Health & Social
      case "medical":
        return <MedicalForm {...formProps} />;
      case "ong":
        return <OngForm {...formProps} />;
      case "crowdfunding":
        return <CrowdfundingForm {...formProps} />;
      case "community":
        return <CommunityForm {...formProps} />;
      case "petition":
        return <PetitionForm {...formProps} />;
      // Wellness & Lifestyle
      case "travel":
        return <TravelForm {...formProps} />;
      case "car":
        return <CarForm {...formProps} />;
      case "yoga":
        return <YogaForm {...formProps} />;
      case "exercise":
        return <ExerciseForm {...formProps} />;
      case "recipe":
        return <RecipeForm {...formProps} />;
      case "fashion":
        return <FashionForm {...formProps} />;
      case "beauty":
        return <BeautyForm {...formProps} />;
      default:
        return null;
    }
  };

  const handleRemoveExistingImage = (index) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingVideo = (index) => {
    setExistingVideos(prev => prev.filter((_, i) => i !== index));
  };

  const postSubmit = async () => {
    const validationErrors = validateEditorialData();
    if (validationErrors.length > 0) {
      setError(validationErrors.join("\n"));
      return;
    }

    setLoading(true);

    try {
      let finalImages = [...existingImages];
      let finalVideos = [...existingVideos];

      // Upload new images if any
      if (images && images.length > 0) {
        const mediaBlobs = images.map((media) => dataURItoBlob(media));
        const path = `${user.username}/post_media`;
        let formData = new FormData();
        formData.append("path", path);
        mediaBlobs.forEach((blob) => {
          formData.append("file", blob);
        });

        const uploadResult = await uploadPostMedia(formData, path, user.token);

        if (uploadResult.error) {
          setError(uploadResult.error);
          setLoading(false);
          return;
        }

        if (uploadResult.images) {
          finalImages = [...finalImages, ...uploadResult.images];
        }
        if (uploadResult.videos) {
          finalVideos = [...finalVideos, ...uploadResult.videos];
        }
      }

      // Prepare update data
      const updateData = {
        text,
        background,
        images: finalImages,
        videos: finalVideos,
      };

      // Add editorial data if not facebook
      if (editorialType !== "facebook") {
        const dataField = dataFieldMap[editorialType];
        if (dataField) {
          updateData[dataField] = editorialData;
        }
      }

      const response = await updatePost(post._id, updateData, user.token);

      setLoading(false);

      if (response.status === "ok") {
        if (onPostUpdated) {
          onPostUpdated(response.data);
        }
        setVisible(false);
      } else {
        setError(response.message || "Erro ao atualizar post");
      }
    } catch (err) {
      setLoading(false);
      setError("Erro ao atualizar post");
    }
  };

  return (
    <div className="blur">
      <div className="postBox edit_post_box" ref={popup}>
        {error && <PostError error={error} setError={setError} />}
        <div className="box_header">
          <div
            className="small_circle"
            onClick={() => setVisible(false)}
          >
            <i className="exit_icon"></i>
          </div>
          <span>Editar {getEditorialTypeName()}</span>
        </div>

        <div className="box_profile">
          <img src={user?.picture} alt="" className="box_profile_img" />
          <div className="box_col">
            <div className="box_profile_name">
              {user?.first_name} {user?.last_name}
            </div>
            <div className="box_privacy">
              <img src="../../../icons/public.png" alt="" />
              <span>Public</span>
              <i className="arrowDown_icon"></i>
            </div>
          </div>
          {editorialType !== "facebook" && (
            <div
              className="editorial_type_badge"
              style={{
                background: getEditorialTypeColor(),
              }}
            >
              {getEditorialTypeName()}
            </div>
          )}
        </div>

        {/* Existing media preview */}
        {(existingImages.length > 0 || existingVideos.length > 0) && !showPrev && (
          <div className="existing_media_preview">
            <div className="existing_media_header">
              <span>Midia atual</span>
            </div>
            <div className="existing_media_grid">
              {existingImages.map((img, index) => (
                <div key={`img-${index}`} className="existing_media_item">
                  <img src={img.url} alt="" />
                  <div
                    className="existing_media_remove"
                    onClick={() => handleRemoveExistingImage(index)}
                  >
                    <i className="exit_icon"></i>
                  </div>
                </div>
              ))}
              {existingVideos.map((video, index) => (
                <div key={`vid-${index}`} className="existing_media_item video_item">
                  <video src={video.url} />
                  <div className="video_indicator">
                    <i className="playV_icon"></i>
                  </div>
                  <div
                    className="existing_media_remove"
                    onClick={() => handleRemoveExistingVideo(index)}
                  >
                    <i className="exit_icon"></i>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!showPrev ? (
          <EmojiPickerBackgrounds
            text={text}
            user={user}
            setText={setText}
            showPrev={showPrev}
            setBackground={setBackground}
            background={background}
          />
        ) : (
          <ImagePreview
            text={text}
            user={user}
            setText={setText}
            showPrev={showPrev}
            images={images}
            setImages={setImages}
            mediaTypes={mediaTypes}
            setMediaTypes={setMediaTypes}
            setShowPrev={setShowPrev}
            setError={setError}
          />
        )}

        {editorialType !== "facebook" && renderEditorialForm()}

        <AddToYourPost setShowPrev={setShowPrev} />

        <button
          className={`post_submit ${!canSubmit() ? "post_submit_disabled" : ""}`}
          onClick={postSubmit}
          disabled={loading}
        >
          {loading ? <PulseLoader color="#fff" size={5} /> : "Salvar Alteracoes"}
        </button>
      </div>
    </div>
  );
}
